import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import RightDrawer from "../components/common/RightDrawer";
import Modal from "../components/common/Modal";
import { FiSun, FiMoon } from "react-icons/fi";
import { useTheme } from "../theme/ThemeProvider";
import { useAuth } from "../context/AuthContext";

const SIDEBAR_KEY = "sidebarOpen";

const initialSidebarOpen = () => {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem(SIDEBAR_KEY);
  if (stored !== null) return stored === "true";
  return window.matchMedia("(min-width: 768px)").matches;
};

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);
  const { logout } = useAuth();

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(SIDEBAR_KEY, String(sidebarOpen));
  }, [sidebarOpen]);

  const toggleSidebar = () => setSidebarOpen((o) => !o);
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-slate-100 text-ink dark:bg-slate-900 transition-colors duration-200">
      <Sidebar
        open={sidebarOpen}
        onClose={closeSidebar}
        onLogout={logout}
      />

      <div
        className={`min-h-screen bg-slate-50 dark:bg-slate-900 transition-[margin-left] duration-300 ease-out ${
          sidebarOpen ? "md:ml-64" : "ml-0"
        }`}
      >
        <Topbar onToggle={toggleSidebar} isSidebarOpen={sidebarOpen} />

        <main className="p-4">
          <div className="card p-4 dark:bg-slate-800 dark:border-slate-700">
            <Outlet />
          </div>
        </main>
      </div>

      <RightDrawer />
      <Modal />
    </div>
  );
}

function Topbar({ onToggle, isSidebarOpen = false }) {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";

  return (
    <header className="sticky top-0 z-30 flex items-center justify-between px-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 h-14 transition-colors duration-200">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={`menu-toggle ${isSidebarOpen ? "is-open" : ""}`}
          aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
          aria-controls="app-sidebar"
          aria-expanded={isSidebarOpen ? "true" : "false"}
          onClick={onToggle}
        >
          <svg
            className="menu-toggle-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line className="menu-line menu-line-top" x1="4" y1="7" x2="20" y2="7" />
            <line className="menu-line menu-line-mid" x1="4" y1="12" x2="20" y2="12" />
            <line className="menu-line menu-line-bot" x1="4" y1="17" x2="20" y2="17" />
          </svg>
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          role="switch"
          aria-checked={isDark}
          onClick={() => setTheme(isDark ? "light" : "dark")}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          className="group flex items-center gap-2.5 rounded-full p-0.5 focus:outline-none"
        >
          <span className="hidden sm:inline text-sm font-medium text-slate-700 dark:text-slate-200">
            {isDark ? "Dark" : "Light"}
          </span>

          <span
            className={`relative inline-flex h-8 w-[60px] shrink-0 items-center rounded-full p-1
                        transition-colors duration-500 ease-out
                        group-focus-visible:ring-2 group-focus-visible:ring-emerald-500/50 group-focus-visible:ring-offset-2 dark:group-focus-visible:ring-offset-slate-800
                        ${isDark ? "bg-gradient-to-r from-slate-800 to-indigo-950" : "bg-gradient-to-r from-sky-300 to-sky-400"}`}
          >
            {}
            <span className={`pointer-events-none absolute w-1 h-1 rounded-full bg-white transition-opacity duration-500 ${isDark ? "opacity-70" : "opacity-0"}`} style={{ left: 12, top: 7 }} />
            <span className={`pointer-events-none absolute w-0.5 h-0.5 rounded-full bg-white transition-opacity duration-500 delay-150 ${isDark ? "opacity-60" : "opacity-0"}`} style={{ left: 22, top: 18 }} />
            <span className={`pointer-events-none absolute w-0.5 h-0.5 rounded-full bg-white transition-opacity duration-500 delay-300 ${isDark ? "opacity-50" : "opacity-0"}`} style={{ left: 10, top: 20 }} />

            {}
            <span className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white/70 blur-[2px] transition-opacity duration-300 ${isDark ? "opacity-0" : "opacity-90"}`} />

            {}
            <span
              className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md
                          transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]
                          group-hover:scale-110 group-active:scale-95
                          ${isDark ? "translate-x-[28px]" : "translate-x-0"}`}
            >
              <FiSun className={`absolute w-3.5 h-3.5 text-amber-500 transition-all duration-300 ${isDark ? "scale-0 rotate-90 opacity-0" : "scale-100 rotate-0 opacity-100"}`} />
              <FiMoon className={`absolute w-3.5 h-3.5 text-indigo-600 transition-all duration-300 ${isDark ? "scale-100 rotate-0 opacity-100" : "scale-0 -rotate-90 opacity-0"}`} />
            </span>
          </span>
        </button>
      </div>
    </header>
  );
}
